import { useRef } from "react";
import { RangeCalendar } from "@datapay-by-datacom/design-system";
import {
  Popover,
  Dialog,
  OverlayArrow,
} from "react-aria-components";
import {
  parseDate,
  today,
  getLocalTimeZone,
  startOfMonth,
  type CalendarDate,
} from "@internationalized/date";
import type { RangeValue } from "@react-types/shared";
import { PillButton } from "./PillButton";
import styles from "./DatePill.module.css";

interface DatePreset {
  label: string;
  getRange: () => { start: string | null; end: string | null };
}

function daysAgo(days: number): string {
  const d = today(getLocalTimeZone()).subtract({ days });
  return d.toString();
}

function todayISO(): string {
  return today(getLocalTimeZone()).toString();
}

const DATE_PRESETS: DatePreset[] = [
  {
    label: "All time",
    getRange: () => ({ start: null, end: null }),
  },
  {
    label: "Last 7 days",
    getRange: () => ({ start: daysAgo(7), end: todayISO() }),
  },
  {
    label: "Last 30 days",
    getRange: () => ({ start: daysAgo(30), end: todayISO() }),
  },
  {
    label: "This month",
    getRange: () => ({
      start: startOfMonth(today(getLocalTimeZone())).toString(),
      end: todayISO(),
    }),
  },
  {
    label: "Last 3 months",
    getRange: () => ({ start: daysAgo(90), end: todayISO() }),
  },
];

function formatShortDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString("en-NZ", { month: "short", day: "numeric" });
}

function formatDateValue(
  dateFrom: string | null,
  dateTo: string | null,
): string {
  if (!dateFrom && !dateTo) return "All time";
  if (dateFrom && dateTo)
    return `${formatShortDate(dateFrom)} \u2013 ${formatShortDate(dateTo)}`;
  if (dateFrom) return `From ${formatShortDate(dateFrom)}`;
  return `Until ${formatShortDate(dateTo!)}`;
}

function toCalendarDate(iso: string): CalendarDate {
  return parseDate(iso.slice(0, 10));
}

export interface DatePillProps {
  dateFrom: string | null;
  dateTo: string | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (dateFrom: string | null, dateTo: string | null) => void;
}

export function DatePill({
  dateFrom,
  dateTo,
  isOpen,
  onOpenChange,
  onChange,
}: DatePillProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const isActive = dateFrom !== null || dateTo !== null;

  function handlePresetClick(preset: DatePreset) {
    const range = preset.getRange();
    onChange(range.start, range.end);
    onOpenChange(false);
  }

  function handleCalendarChange(value: RangeValue<CalendarDate>) {
    if (!value.start || !value.end) return;
    onChange(value.start.toString(), value.end.toString());
    onOpenChange(false);
  }

  const calendarValue =
    dateFrom && dateTo
      ? { start: toCalendarDate(dateFrom), end: toCalendarDate(dateTo) }
      : undefined;

  return (
    <>
      <PillButton
        label="Date"
        value={formatDateValue(dateFrom, dateTo)}
        isActive={isActive}
        isOpen={isOpen}
        triggerRef={triggerRef}
        onClick={() => onOpenChange(!isOpen)}
        testId="date-pill"
      />
      <Popover
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="bottom"
        className={styles.datePopover}
      >
        <OverlayArrow>
          <svg width={12} height={12} viewBox="0 0 12 12">
            <path d="M0 0 L6 6 L12 0" />
          </svg>
        </OverlayArrow>
        <Dialog className={styles.dateDialog}>
          <div className={styles.dateLayout} data-testid="date-options">
            <div className={styles.datePresets}>
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  className={styles.datePresetButton}
                  onClick={() => handlePresetClick(preset)}
                  data-testid={`date-preset-${preset.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className={styles.dateCalendar}>
              <RangeCalendar
                aria-label="Date range"
                value={calendarValue}
                onChange={handleCalendarChange}
                maxValue={today(getLocalTimeZone())}
              />
            </div>
          </div>
        </Dialog>
      </Popover>
    </>
  );
}
